import type { GraphAppearanceConfig } from '../entities/graph-appearance-config';

export type ConnectionStrategy = 'relation-first' | 'balanced' | 'label-first';
export type ConnectionDisplayMode = 'all' | 'tag-derived' | 'explicit-only' | 'tag-derived-focus';
export type WorkspaceGraphMode = '2d' | '3d';
export type WorkspaceMode = WorkspaceGraphMode | 'outline';

export interface WorkspaceModePreference {
	workspaceMode: WorkspaceMode;
	lastGraphMode: WorkspaceGraphMode;
}

export const DEFAULT_CONNECTION_STRATEGY: ConnectionStrategy = 'balanced';
export const DEFAULT_CONNECTION_DISPLAY_MODE: ConnectionDisplayMode = 'all';
export const DEFAULT_WORKSPACE_MODE_PREFERENCE: WorkspaceModePreference = {
	workspaceMode: '2d',
	lastGraphMode: '2d',
};

export function isConnectionStrategy(value: unknown): value is ConnectionStrategy {
	return value === 'relation-first' || value === 'balanced' || value === 'label-first';
}

export function isConnectionDisplayMode(value: unknown): value is ConnectionDisplayMode {
	return value === 'all' || value === 'tag-derived' || value === 'explicit-only' || value === 'tag-derived-focus';
}

export function isWorkspaceGraphMode(value: unknown): value is WorkspaceGraphMode {
	return value === '2d' || value === '3d';
}

export function isWorkspaceMode(value: unknown): value is WorkspaceMode {
	return isWorkspaceGraphMode(value) || value === 'outline';
}

export function normalizeWorkspaceModePreference(
	preference?: Partial<WorkspaceModePreference> | null
): WorkspaceModePreference {
	const workspaceMode = isWorkspaceMode(preference?.workspaceMode)
		? preference.workspaceMode
		: DEFAULT_WORKSPACE_MODE_PREFERENCE.workspaceMode;
	const rawLastGraphMode = preference?.lastGraphMode;
	const lastGraphMode = workspaceMode === 'outline'
		? (isWorkspaceGraphMode(rawLastGraphMode)
			? rawLastGraphMode
			: DEFAULT_WORKSPACE_MODE_PREFERENCE.lastGraphMode)
		: workspaceMode;

	return {
		workspaceMode,
		lastGraphMode,
	};
}

export interface Preferences {
	locale: 'zh-TW' | 'en';
	accessGateGrantedDate?: string;
	connectionStrategy?: ConnectionStrategy;
	connectionDisplayMode?: ConnectionDisplayMode;
	workspaceMode?: WorkspaceMode;
	lastGraphMode?: WorkspaceGraphMode;
	graphAppearanceByGraphId?: Record<string, GraphAppearanceConfig | unknown>;
	[key: string]: unknown;
}
export interface PreferencesRepository { load(): Promise<Partial<Preferences>>; save(preferences: Partial<Preferences>): Promise<void>; get<K extends keyof Preferences>(key: K): Promise<Preferences[K] | undefined>; set<K extends keyof Preferences>(key: K, value: Preferences[K]): Promise<void>; }
